import type {FC} from 'react';
import {useRef} from 'react';
import {DragSourceMonitor, useDrag, useDrop} from 'react-dnd';
import type {Identifier} from 'dnd-core';

const style = {
  cursor: 'move',
};

export interface DndWrapperProps {
  id: number;
  children: JSX.Element;
  index: number;
  onMove: (dragIndex: number, hoverIndex: number) => void;
  onDrop: () => void;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export const DndWrapper: FC<DndWrapperProps> = ({id, index, children, onMove, onDrop}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [{handlerId}, drop] = useDrop<DragItem, void, {handlerId: Identifier | null}>({
    accept: 'item',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    drop() {
      onDrop();
    },
    hover(item: DragItem) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      onMove(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{isDragging}, drag] = useDrag({
    type: 'item',
    item: () => {
      return {id, index};
    },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0.5 : 1;
  drag(drop(ref));

  return (
    <div ref={ref} style={{...style, opacity}} data-handler-id={handlerId}>
      {children}
    </div>
  );
};
